import { useSnackbar } from 'notistack'
import {
  useGqlToggleCompanyInterestInMutation,
  GqlCompanyInformationNotCachedQueryDocument,
  GqlRelatedCompaniesDocument,
  GqlRelatedCompaniesQuery,
  GqlPopularCompanyInformationsDocument,
  GqlCompaniesDocument,
} from 'generated/graphql'

const useToggleCompanyInterest = () => {
  const [toggleCompanyInterestMutation, { loading }] =
    useGqlToggleCompanyInterestInMutation()
  const { enqueueSnackbar } = useSnackbar()

  const toggleCompanyInterest = (
    companyId: string,
    currentIsInterested?: boolean,
    relatedCompanyId?: string,
  ) => {
    const newIsInterested = !currentIsInterested
    toggleCompanyInterestMutation({
      variables: {
        input: {
          companyId,
          isInterestedIn: newIsInterested,
        },
      },
      fetchPolicy: 'network-only',
      refetchQueries: [
        GqlCompanyInformationNotCachedQueryDocument,
        GqlPopularCompanyInformationsDocument,
        GqlCompaniesDocument,
      ],
      update: (cache, { data }) => {
        if (!data?.toggleCompanyInterestIn || !relatedCompanyId) return

        const targetQueryOptions = {
          query: GqlRelatedCompaniesDocument,
          variables: {
            companyId: relatedCompanyId,
          },
        }

        const cachedRelatedCompanies =
          cache.readQuery<GqlRelatedCompaniesQuery>(targetQueryOptions)

        if (cachedRelatedCompanies?.relatedCompanies) {
          const updatedRelatedCompanies =
            cachedRelatedCompanies.relatedCompanies.map((company) => {
              if (company.id === companyId) {
                return {
                  ...company,
                  interestCount: newIsInterested
                    ? company.interestCount + 1
                    : Math.max(company.interestCount - 1, 0),
                  companyInformation: company.companyInformation
                    ? {
                        ...company.companyInformation,
                        viewerInterestedIn: newIsInterested,
                      }
                    : null,
                }
              }
              return company
            })

          cache.writeQuery<GqlRelatedCompaniesQuery>({
            ...targetQueryOptions,
            data: {
              ...cachedRelatedCompanies,
              relatedCompanies: updatedRelatedCompanies,
            },
          })
        }
      },
      onCompleted: (data) => {
        const isInterested = data.toggleCompanyInterestIn.isInterestedIn
        enqueueSnackbar(
          isInterested
            ? '해당 기업이 관심기업으로 등록되었습니다.'
            : '해당 기업이 관심기업에서 해제되었습니다.',
          {
            variant: 'success',
          },
        )
      },
      onError: (error) => {
        enqueueSnackbar(error.message, {
          variant: 'error',
        })
      },
    })
  }

  return {
    toggleCompanyInterest,
    isLoading: loading,
  }
}

export default useToggleCompanyInterest
